import { ModalHeader } from 'src/modules/common/components/Modal/ModalHeader.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { ModalBody } from 'src/modules/common/components/Modal/ModalBody.tsx';
import { ModalFooter } from 'src/modules/common/components/Modal/ModalFooter.tsx';
import { Button } from 'src/common/Button/Button.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { Modal } from 'src/modules/common/components/Modal/Modal.tsx';
import { targetUnits } from 'src/modules/user-input/targetUnits.ts';
import { AllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping.ts';
import { HandleModalTransferEvent, ModalState } from 'src/modules/user-input/components/FlowCalculatorModals.tsx';
import { useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { Unit } from 'uom';
import { CalculatorModalLayout } from 'src/modules/common/layouts/CalculatorModalLayout.tsx';
import {
    CalculatorModalMainSection,
} from 'src/modules/common/components/CalculatorModalMainSection/CalculatorModalMainSection.tsx';
import { numberFormatter } from 'src/utils/numberFormatter.ts';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField.tsx';
import { authorityValues } from 'src/modules/user-input/calcMaxDP/authorityValues.ts';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { reducer } from 'src/modules/user-input/calcMaxDP/reducer.ts';
import { initialState } from 'src/modules/user-input/calcMaxDP/initialState.ts';
import { convert } from 'src/modules/user-input/convert.ts';
import { calcMaxDp, calcMaxDp3way } from 'src/modules/user-input/calcMaxDP/calcMaxDp.ts';
import { Units } from 'uom-units';

const MODAL_NAME = 'maxDp';

export type DPMVState = {
    value: string;
    unit: Unit.Unit<'Pressure'>;
};

export type HandleMaxDpModalTransferEvent = HandleModalTransferEvent & { dpmv: DPMVState };

export const MaxDpModal = ({
    modalsState,
    handleModalClose,
    handleModalTransfer,
    allowedUnits,
    dpmv,
    is3WaySelected,
} : {
    is3WaySelected: boolean;
    allowedUnits: AllowedUnits;
    modalsState: ModalState;
    dpmv: DPMVState;
    handleModalClose: () => void;
    handleModalTransfer: (detail: HandleMaxDpModalTransferEvent) => void;
}) => {
    const config = useContext(ConfigContext);

    const targetUnit = modalsState?.unit || targetUnits.dpv100;

    const [unit, setUnit] = useState(targetUnit);

    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        dpmv: dpmv.value,
        dpmvUnit: dpmv.unit,
    });
    const dpv100 = useMemo(() => convert(
        is3WaySelected ? calcMaxDp3way(
            convert(state.dpmv, state.dpmvUnit, targetUnits.dpmv),
            state.pv,
        ) : calcMaxDp(
            convert(state.dpmv, state.dpmvUnit, targetUnits.dpmv),
            state.pv,
        ),
        Units.KiloPascal,
        unit,
    ), [is3WaySelected, state, unit]);

    useEffect(() => {
        setUnit(targetUnit);
    }, [targetUnit]);
    return (
        <Modal
            key={MESSAGES.CalcMaxDPTitle}
            opened={modalsState?.field === MODAL_NAME}
            handleClose={handleModalClose}
        >
            <ModalHeader
                title={MESSAGES.CalcMaxDPTitle}
                handleClose={handleModalClose}
                handleReset={() => {
                    dispatch({ type: 'reset' });
                }}
            >
                <I18n messageId={MESSAGES.CalcMaxDPTitleDescr}/>
            </ModalHeader>

            <ModalBody>
                <CalculatorModalLayout>
                    <CalculatorModalMainSection
                        title={MESSAGES.CalcMaxDPTitleResult}
                        result={numberFormatter(dpv100, config.userSettings.currentLocale)}
                        selectedUnit={unit}
                        allowedUnits={allowedUnits.dpmv}
                        onUnitChange={setUnit}
                    >
                        <DropdownField
                            options={authorityValues}
                            selectedValue={state.pv}
                            onChange={(payload) => {
                                dispatch({ type: 'pv_change', payload });
                            }}
                        >
                            <I18n messageId={MESSAGES.pv}/>
                        </DropdownField>
                        <DecimalField
                            name="dpmv"
                            inputSize="md"
                            labelText={is3WaySelected ? MESSAGES.DPVR : MESSAGES.DPMV}
                            value={state.dpmv}
                            endAdornment={(
                                <UnitSelector
                                    selected={state.dpmvUnit.name}
                                    values={allowedUnits.dpmv}
                                    onChange={(payload) => {
                                        dispatch({ type: 'dpmv_unit', payload });
                                    }}
                                />
                            )}
                            currentLocale={config.userSettings.currentLocale}
                            onValueChange={(payload) => {
                                dispatch({
                                    type: 'dpmv_change',
                                    payload,
                                });
                            }}
                            autoComplete="off"
                        />
                    </CalculatorModalMainSection>
                </CalculatorModalLayout>
            </ModalBody>
            <ModalFooter>
                <Button
                    kind="secondary"
                    fullWidth={true}
                    onClick={handleModalClose}
                >
                    <I18n messageId={MESSAGES.CANCEL}/>
                </Button>
                <Button
                    fullWidth={true}
                    onClick={() => {
                        handleModalTransfer({
                            value: dpv100,
                            unit,
                            dpmv: {
                                value: state.dpmv,
                                unit: state.dpmvUnit,
                            },
                        });
                    }}
                >
                    <I18n messageId={MESSAGES.apply}/>
                </Button>
            </ModalFooter>
        </Modal>
    );
};
